import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@store'
import { postService } from '@services/api/posts'
import apiClient from '@services/api/client'
import toast from 'react-hot-toast'
import avatarPlaceholder from '@assets/images/image_01.png'
import { storageURL, mediaUrl } from '@config'
import { encodeId } from '@utils/idCipher'

const CONDITION_LABELS = {
  neuf: 'Neuf', scelle: 'Scellé', reconditionne: 'Reconditionné',
  quasi_neuf: 'Quasi neuf', excellent: 'Excellent', bon: 'Bon état', moyen: 'État moyen', usage: 'Usagé',
}

const mapPost = (p) => {
  const prixNum     = parseFloat(p.prix) || 0
  const newPriceNum = parseFloat(p.new_price) || 0
  const discount    = newPriceNum > prixNum && newPriceNum > 0
    ? Math.round((1 - prixNum / newPriceNum) * 100) : 0
  return {
    id:                  p.post_id,
    name:                p.libele,
    price:               p.prix ? `${prixNum.toLocaleString('fr-FR')} FCFA` : '',
    oldPrice:            newPriceNum > prixNum ? `${newPriceNum.toLocaleString('fr-FR')} FCFA` : null,
    discount,
    etat_appareil:       p.etat_appareil || '',
    etat_appareil_label: CONDITION_LABELS[p.etat_appareil] || '',
    image:               (() => { const f = p.fichiers?.find(f => f.is_main) ?? p.fichiers?.[0]; return f?.chemin ? mediaUrl(f.chemin) : null })(),
    views:               p.vues || 0,
    likes:               p.post_likes_count || 0,
    date:                p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR') : '',
    status:              p.status,
  }
}

const mapVideo = (p) => {
  const videoFile = p.fichiers?.find(f => /\.(mp4|mov|avi|webm)$/i.test(f.chemin))
  const thumbFile = p.fichiers?.find(f => /\.(jpg|jpeg|png|gif|webp)$/i.test(f.chemin))
  return {
    id:          p.post_id,
    name:        p.libele,
    description: p.description,
    videoUrl:    videoFile ? mediaUrl(videoFile.chemin) : null,
    thumbnail:   thumbFile ? mediaUrl(thumbFile.chemin) : null,
    views:       p.vues || 0,
    likes:       p.post_likes_count || 0,
    date:        p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR') : '',
    status:      p.status,
  }
}

const mapBanner = (p) => ({
  id: p.post_id,
  name: p.libele,
  description: p.description,
  link: p.siteWeb,
  image: p.fichiers?.[0]?.chemin ? mediaUrl(p.fichiers[0].chemin) : null,
  date: p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR') : '',
  status: p.status,
})

function MyDashboardPage() {
  const { user, userType } = useAuthStore()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('articles') // articles, banners, videos
  const [articleFilter, setArticleFilter] = useState('all') // all, online, completed, archived
  const [bannerFilter, setBannerFilter] = useState('all') // all, online, completed, archived
  const [videoFilter, setVideoFilter] = useState('all') // all, online, completed, archived
  const [myProducts, setMyProducts] = useState([])
  const [myBanners, setMyBanners] = useState([])
  const [myVideos, setMyVideos] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingBanners, setLoadingBanners] = useState(false)
  const [loadingVideos, setLoadingVideos] = useState(false)
  const [isFreeTrialActive, setIsFreeTrialActive] = useState(false)
  const [freeTrialDaysLeft, setFreeTrialDaysLeft] = useState(0)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [productToDelete, setProductToDelete] = useState(null)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [productToArchive, setProductToArchive] = useState(null)
  const [isUnarchiving, setIsUnarchiving] = useState(false)
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalViews: 0,
    totalLikes: 0,
    totalComments: 0,
    totalSubscribers: 0
  })

  // États pour le profil
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    bio: '',
    location: '',
    website: ''
  })

  // États pour les paramètres
  const [settingsData, setSettingsData] = useState({
    notifications: true,
    emailNotifications: true,
    privateProfile: false,
    showEmail: false,
    showPhone: true
  })

  useEffect(() => {
    if (user?.id || user?.client_id) {
      fetchMyProducts()
      checkFreeTrial()
      if (userType === 'admin') fetchSubscribersCount()
    }
  }, [user?.id, user?.client_id])

  // Charger bannières et vidéos quand on change d'onglet
  useEffect(() => {
    if (activeTab === 'banners' && myBanners.length === 0 && user) {
      fetchMyBanners()
    } else if (activeTab === 'videos' && myVideos.length === 0 && user) {
      fetchMyVideos()
    }
  }, [activeTab, user])

  const fetchMyBanners = async () => {
    try {
      setLoadingBanners(true)
      const clientId = user?.id || user?.client_id
      const result = await postService.getPosts({ type: 3, post_client_id: clientId })
      const raw = result.data || []
      setMyBanners(Array.isArray(raw) ? raw.map(mapBanner) : [])
    } catch (error) {
      console.error('Erreur lors du chargement des bannières:', error)
    } finally {
      setLoadingBanners(false)
    }
  }

  const fetchMyVideos = async () => {
    try {
      setLoadingVideos(true)
      const clientId = user?.id || user?.client_id
      const result = await postService.getPosts({ type: 1, post_client_id: clientId })
      const raw = result.data || []
      setMyVideos(Array.isArray(raw) ? raw.map(mapVideo) : [])
    } catch (error) {
      console.error('Erreur lors du chargement des vidéos:', error)
    } finally {
      setLoadingVideos(false)
    }
  }

  const fetchSubscribersCount = async () => {
    try {
      const r = await apiClient.get('/clients/count')
      setStats(prev => ({ ...prev, totalSubscribers: r.count || 0 }))
    } catch {
      setStats(prev => ({ ...prev, totalSubscribers: 0 }))
    }
  }

  const checkFreeTrial = () => {
    const createdDate = user?.created_at ? new Date(user.created_at) : null
    if (!createdDate) return
    const diffDays = Math.floor((Date.now() - createdDate) / (1000 * 60 * 60 * 24))
    const daysLeft = Math.max(0, 7 - diffDays)
    setIsFreeTrialActive(daysLeft > 0)
    setFreeTrialDaysLeft(daysLeft)
  }

  const fetchMyProducts = async () => {
    try {
      setLoading(true)
      const clientId = user?.id || user?.client_id || 1
      const data = await postService.getPosts({
        type: 2,
        post_client_id: clientId
      })

      const raw = data.data || data.posts || data || []
      const products = Array.isArray(raw) ? raw.map(mapPost) : []
      setMyProducts(products)

      // Calculer les stats (uniquement pour les posts publiés)
      const activeProducts = products.filter(p => p.status === 2)
      const totalViews = activeProducts.reduce((sum, p) => sum + parseInt(p.views || 0), 0)
      const totalLikes = activeProducts.reduce((sum, p) => sum + parseInt(p.likes || 0), 0)
      const totalComments = 0
      
      setStats(prev => ({
        ...prev,
        totalProducts: activeProducts.length,
        totalViews,
        totalLikes,
        totalComments
      }))
    } catch (err) {
      console.error('Erreur:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleEditProduct = (product) => {
    // Rediriger vers la page d'édition avec l'ID du produit
    navigate(`/edit-post/${product.id}`)
  }

  const handleDeleteProduct = async (id) => {
    setProductToDelete(id)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (productToDelete) {
      try {
        await postService.deletePost(productToDelete)
        // Recharger les données selon l'onglet actif
        if (activeTab === 'videos') {
          await fetchMyVideos()
        } else if (activeTab === 'banners') {
          await fetchMyBanners()
        } else {
          await fetchMyProducts()
        }
        toast.success(activeTab === 'videos' ? 'Vidéo supprimée avec succès !' : 'Article supprimé avec succès !')
        setShowDeleteModal(false)
        setProductToDelete(null)
      } catch (error) {
        console.error('Erreur lors de la suppression:', error)
        toast.error('Erreur lors de la suppression')
      }
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setProductToDelete(null)
  }

  const handleArchivePost = (id, isArchived) => {
    setProductToArchive(id)
    setIsUnarchiving(isArchived)
    setShowArchiveModal(true)
  }

  const confirmArchive = async () => {
    if (productToArchive) {
      try {
        await postService.archivePost(productToArchive)
        // Recharger les données selon l'onglet actif
        if (activeTab === 'videos') {
          await fetchMyVideos()
        } else if (activeTab === 'banners') {
          await fetchMyBanners()
        } else {
          await fetchMyProducts()
        }
        const itemType = activeTab === 'videos' ? 'Vidéo' : activeTab === 'banners' ? 'Bannière' : 'Article'
        toast.success(`${itemType} ${isUnarchiving ? 'désarchivée' : 'archivée'} avec succès !`)
        setShowArchiveModal(false)
        setProductToArchive(null)
        setIsUnarchiving(false)
      } catch (error) {
        console.error('Erreur lors de l\'archivage:', error)
        toast.error(`Erreur lors de l'${isUnarchiving ? 'désarchivage' : 'archivage'}`)
      }
    }
  }

  const cancelArchive = () => {
    setShowArchiveModal(false)
    setProductToArchive(null)
    setIsUnarchiving(false)
  }

  const handleSaveProfile = () => {
    // TODO: Envoyer au backend
    toast.success('Profil mis à jour avec succès !')
  }

  const handleSaveSettings = () => {
    // TODO: Envoyer au backend
    toast.success('Paramètres mis à jour avec succès !')
  }

  return (
    <div className="home-font min-h-screen bg-slate-50 py-8">
      <div className="w-[94%] md:w-[80%] mx-auto">

        {/* En-tête */}
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-5">
          <div>
            <p className="text-sm font-bold uppercase tracking-widest text-[#B8860B]">Mon espace</p>
            <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#002A4E]">
              Bonjour{user?.prenom ? ` ${user.prenom}` : ''} 👋
            </h1>
            <p className="mt-1 text-slate-500">Gérez vos publications et suivez leurs performances.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate('/kudhi-market')}
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-[#002A4E] transition-colors hover:border-slate-300 hover:bg-slate-50"
            >
              <DashIcon d={DASH_PATHS.cart} />
              Voir le Market
            </button>
            <button
              onClick={() => navigate(activeTab === 'banners' ? '/create-banner' : activeTab === 'videos' ? '/create-video' : '/create-post')}
              className="inline-flex items-center gap-2 rounded-md bg-[#FFD700] px-4 py-2.5 text-sm font-bold text-[#002A4E] shadow-sm shadow-amber-300/60 transition-all hover:-translate-y-0.5 hover:bg-[#F5C800]"
            >
              <DashIcon d={DASH_PATHS.plus} stroke={2.4} />
              Nouvelle publication
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="mb-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {[
            { label: 'Publications', value: stats.totalProducts, icon: DASH_PATHS.box, tone: 'bg-[#FFD700] text-[#002A4E]' },
            { label: 'Vues', value: stats.totalViews, icon: DASH_PATHS.eye, tone: 'bg-emerald-50 text-emerald-600' },
            { label: 'J\'aime', value: stats.totalLikes, icon: DASH_PATHS.heart, tone: 'bg-rose-50 text-rose-600' },
            { label: 'Commentaires', value: stats.totalComments, icon: DASH_PATHS.chat, tone: 'bg-sky-50 text-sky-600' },
            { label: 'Abonnés', value: stats.totalSubscribers || 0, icon: DASH_PATHS.users, tone: 'bg-violet-50 text-violet-600' },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-4 rounded-md bg-white p-4 sm:p-5 ring-1 ring-slate-200/70">
              <span className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md ${s.tone}`}>
                <DashIcon d={s.icon} className="w-5 h-5" />
              </span>
              <div className="min-w-0">
                <p className="text-2xl font-extrabold leading-none tracking-tight text-[#002A4E] tabular-nums">{(s.value ?? 0).toLocaleString('fr-FR')}</p>
                <p className="mt-1 truncate text-sm text-slate-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Onglets principaux: Articles, Bannières, Vidéos */}
        <div className="mb-6 flex gap-1 overflow-x-auto scrollbar-hide border-b border-slate-200">
          {[
            ['articles', 'Articles', DASH_PATHS.image],
            ['banners', 'Bannières', DASH_PATHS.banner],
            ['videos', 'Vidéos', DASH_PATHS.video],
          ].map(([key, label, icon]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`relative inline-flex flex-shrink-0 items-center gap-2 px-4 sm:px-6 py-3.5 text-sm sm:text-base font-bold transition-colors ${
                activeTab === key ? 'text-[#002A4E]' : 'text-slate-400 hover:text-[#002A4E]'
              }`}
            >
              <span className={`flex h-7 w-7 items-center justify-center rounded-md ${activeTab === key ? 'bg-[#FFD700] text-[#002A4E]' : 'bg-slate-100 text-slate-400'}`}>
                <DashIcon d={icon} className="w-4 h-4" />
              </span>
              {label}
              {activeTab === key && <span className="absolute inset-x-0 -bottom-px h-[3px] rounded-full bg-[#F5B700]" />}
            </button>
          ))}
        </div>

        {/* Onglet Articles */}

        {activeTab === 'articles' && (
        <div className="rounded-md bg-white ring-1 ring-slate-200/70 mb-6 p-3 sm:p-6">
          <div>
            <div>
                {/* Sous-onglets pour filtrer les articles */}
                <div className="flex items-center gap-2 mb-6 flex-wrap">
                  <button
                    onClick={() => setArticleFilter('all')}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold transition-all ${
                      articleFilter === 'all'
                        ? 'bg-[#002A4E] text-white shadow-sm'
                        : 'bg-white text-slate-600 hover:text-[#002A4E] border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    Tous
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myProducts.length}
                    </span>
                  </button>
                  <button
                    onClick={() => setArticleFilter('online')}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold transition-all ${
                      articleFilter === 'online'
                        ? 'bg-[#002A4E] text-white shadow-sm'
                        : 'bg-white text-slate-600 hover:text-[#002A4E] border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    En ligne
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myProducts.filter(p => p.status === 2).length}
                    </span>
                  </button>
                  <button
                    onClick={() => setArticleFilter('completed')}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold transition-all ${
                      articleFilter === 'completed'
                        ? 'bg-[#002A4E] text-white shadow-sm'
                        : 'bg-white text-slate-600 hover:text-[#002A4E] border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    En attente
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myProducts.filter(p => p.status === 1).length}
                    </span>
                  </button>
                  <button
                    onClick={() => setArticleFilter('archived')}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold transition-all ${
                      articleFilter === 'archived'
                        ? 'bg-[#002A4E] text-white shadow-sm'
                        : 'bg-white text-slate-600 hover:text-[#002A4E] border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    Archivés
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myProducts.filter(p => p.status === 3).length}
                    </span>
                  </button>
                </div>

                <div className="flex items-center justify-between mb-6 gap-3">
                  <h2 className="text-lg sm:text-2xl font-bold text-[#002A4E] leading-tight">
                    Mes Articles
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      ({myProducts.filter(product => {
                        if (articleFilter === 'all') return true
                        if (articleFilter === 'online') return product.status === 2
                        if (articleFilter === 'completed') return product.status === 1
                        if (articleFilter === 'archived') return product.status === 3
                        return true
                      }).length})
                    </span>
                  </h2>
                  <button
                    onClick={() => navigate('/create-post')}
                    className="flex-shrink-0 flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-[#FFD700] text-[#002A4E] font-bold rounded-md hover:bg-[#F5C800] transition-all shadow-md hover:shadow-lg text-sm sm:text-base"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="whitespace-nowrap">Nouvel article</span>
                  </button>
                </div>

                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="bg-white border border-gray-200 rounded-md overflow-hidden">
                        {/* Image skeleton */}
                        <div className="relative w-full h-40 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse">
                          {/* Boutons action skeleton */}
                          <div className="absolute top-2 right-2 flex gap-2">
                            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
                            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
                            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
                          </div>
                        </div>
                        
                        {/* Content skeleton */}
                        <div className="p-4 space-y-3">
                          {/* Title skeleton */}
                          <div className="h-5 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                          
                          {/* Price skeleton */}
                          <div className="h-6 bg-gray-200 rounded w-2/5 animate-pulse"></div>
                          
                          {/* Stats skeleton */}
                          <div className="flex items-center justify-between pt-1">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1">
                                <div className="w-4 h-4 bg-gray-200 rounded-full animate-pulse"></div>
                                <div className="h-3 bg-gray-200 rounded w-8 animate-pulse"></div>
                              </div>
                              <div className="flex items-center gap-1">
                                <div className="w-4 h-4 bg-gray-200 rounded-full animate-pulse"></div>
                                <div className="h-3 bg-gray-200 rounded w-6 animate-pulse"></div>
                              </div>
                            </div>
                            <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : myProducts.filter(product => {
                  if (articleFilter === 'all') return true
                  if (articleFilter === 'online') return product.status === 2
                  if (articleFilter === 'completed') return product.status === 1
                  if (articleFilter === 'archived') return product.status === 3
                  return true
                }).length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-md border-2 border-dashed border-slate-200">
                    <svg className="w-20 h-20 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">
                      {articleFilter === 'all' && "Aucun article"}
                      {articleFilter === 'online' && "Aucun article en ligne"}
                      {articleFilter === 'completed' && "Aucun article en attente"}
                      {articleFilter === 'archived' && "Aucun article archivé"}
                    </h3>
                    <p className="text-gray-500 mb-6">
                      {articleFilter === 'all' && "Commencez par publier votre premier article"}
                      {articleFilter === 'online' && "Vous n'avez pas d'articles actuellement en ligne"}
                      {articleFilter === 'completed' && "Vous n'avez pas d'articles en attente de validation"}
                      {articleFilter === 'archived' && "Vous n'avez pas d'articles archivés"}
                    </p>
                    <button
                      onClick={() => navigate('/create-post')}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#FFD700] text-[#002A4E] font-bold rounded-md hover:bg-[#F5C800] transition-all shadow-md hover:shadow-lg"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Créer un article
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {myProducts.filter(product => {
                      if (articleFilter === 'all') return true
                      if (articleFilter === 'online') return product.status === 2
                      if (articleFilter === 'completed') return product.status === 1
                      if (articleFilter === 'archived') return product.status === 3
                      return true
                    }).map((product) => (
                      <div key={product.id} className={`bg-white border border-gray-200 rounded-md overflow-hidden hover:shadow-lg transition-all flex flex-col ${product.status === 3 ? 'opacity-60 grayscale' : ''}`}>
                        <div className="relative bg-white">
                          <div className="w-full h-56 flex items-center justify-center">
                            <img
                              src={product.image || avatarPlaceholder}
                              alt={product.name}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.onerror = null; e.target.src = avatarPlaceholder }}
                            />
                          </div>
                          {/* Badge de statut */}
                          <div className="absolute top-2 left-2 flex flex-col gap-1">
                            {product.status === 2 && <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">EN LIGNE</span>}
                            {product.status === 1 && <span className="bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded">EN ATTENTE</span>}
                            {product.status === 0 && <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">REJETÉ</span>}
                            {product.status === 3 && <span className="bg-gray-600 text-white text-xs font-bold px-3 py-1 rounded shadow-lg">ARCHIVÉ</span>}
                            {product.discount > 0 && <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">-{product.discount}%</span>}
                          </div>
                          {/* Badge état */}
                          {product.etat_appareil_label && (
                            <div className="absolute top-2 right-2">
                              <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded shadow-md">{product.etat_appareil_label}</span>
                            </div>
                          )}
                        </div>
                        <div className="p-4 flex-1 flex flex-col">
                          <h3 className="font-semibold text-[#002A4E] mb-1 line-clamp-2">{product.name}</h3>
                          {product.oldPrice && (
                            <p className="text-gray-400 line-through text-sm mb-0.5">{product.oldPrice}</p>
                          )}
                          <p className="text-red-600 font-bold text-lg mb-3">{product.price}</p>
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                            <div className="flex items-center gap-3">
                              <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                {product.views}
                              </span>
                              <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                                {product.likes}
                              </span>
                            </div>
                            <span>{product.date}</span>
                          </div>
                          
                          {/* Boutons d'actions en bas */}
                          <div className="flex gap-2 mt-auto pt-3 border-t">
                            <button
                              onClick={() => navigate(`/product/${encodeId(product.id)}`)}
                              className="flex-1 p-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                              title="Voir"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleEditProduct(product)}
                              className="flex-1 p-2 bg-green-50 text-green-600 rounded-md hover:bg-green-100 transition-colors flex items-center justify-center gap-1"
                              title="Modifier"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleArchivePost(product.id, product.status === 3)}
                              className="flex-1 p-2 bg-orange-50 text-orange-600 rounded-md hover:bg-orange-100 transition-colors flex items-center justify-center gap-1"
                              title={product.status === 3 ? "Désarchiver" : "Archiver"}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(product.id)}
                              className="flex-1 p-2 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors flex items-center justify-center gap-1"
                              title="Supprimer"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>

          </div>
        </div>
        )}

        {/* Onglet Bannières */}

        {activeTab === 'banners' && (
          <div className="rounded-md bg-white ring-1 ring-slate-200/70 mb-6 p-3 sm:p-6">
            <div>
              {/* Sous-onglets pour filtrer les bannières */}
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setBannerFilter('all')}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold transition-all ${
                      bannerFilter === 'all'
                        ? 'bg-[#002A4E] text-white shadow-sm'
                        : 'bg-white text-slate-600 hover:text-[#002A4E] border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    Tous
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myBanners.length}
                    </span>
                  </button>
                  <button
                    onClick={() => setBannerFilter('online')}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold transition-all ${
                      bannerFilter === 'online'
                        ? 'bg-[#002A4E] text-white shadow-sm'
                        : 'bg-white text-slate-600 hover:text-[#002A4E] border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    En ligne
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myBanners.filter(b => b.status === 2).length}
                    </span>
                  </button>
                  <button
                    onClick={() => setBannerFilter('completed')}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold transition-all ${
                      bannerFilter === 'completed'
                        ? 'bg-[#002A4E] text-white shadow-sm'
                        : 'bg-white text-slate-600 hover:text-[#002A4E] border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    En attente
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myBanners.filter(b => b.status === 1).length}
                    </span>
                  </button>
                  <button
                    onClick={() => setBannerFilter('archived')}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold transition-all ${
                      bannerFilter === 'archived'
                        ? 'bg-[#002A4E] text-white shadow-sm'
                        : 'bg-white text-slate-600 hover:text-[#002A4E] border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    Archivés
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myBanners.filter(b => b.status === 3).length}
                    </span>
                  </button>
                </div>

                <div className='flex w-full items-center justify-between gap-3 mt-4'>
                  <h2 className="text-lg sm:text-2xl font-bold text-[#002A4E] leading-tight">
                    Mes Bannières
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      ({myBanners.length})
                    </span>
                  </h2>
                  <button
                    onClick={() => navigate('/create-banner')}
                    className="flex-shrink-0 flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-[#FFD700] text-[#002A4E] font-bold rounded-md hover:bg-[#F5C800] transition-colors shadow-md text-sm sm:text-base"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="whitespace-nowrap">Nouvelle Bannière</span>
                  </button>
                </div>
                
              </div>

              {loadingBanners ? (
                <div className="grid grid-cols-1 gap-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="bg-white rounded-md overflow-hidden shadow-sm border border-gray-100">
                      <div className="h-48 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse"></div>
                      <div className="p-4 space-y-2">
                        <div className="h-5 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : myBanners.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-500 text-lg mb-4">Aucune bannière</p>
                  <button 
                    onClick={() => navigate('/create-banner')}
                    className="px-6 py-3 bg-[#FFD700] text-[#002A4E] font-bold rounded-md hover:bg-[#F5C800] transition-colors"
                  >
                    Créer votre première bannière
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myBanners.filter(banner => {
                    if (bannerFilter === 'all') return true
                    if (bannerFilter === 'online') return banner.status === 2
                    if (bannerFilter === 'completed') return banner.status === 1
                    if (bannerFilter === 'archived') return banner.status === 3
                    return true
                  }).map((banner) => (
                    <div key={banner.id} className={`bg-white rounded-md overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow ${banner.status === 3 ? 'opacity-60 grayscale' : ''}`}>
                      <div className="relative">
                        {banner.image ? (
                          <img
                            src={banner.image}
                            alt={banner.name || 'Bannière'}
                            className="w-full h-48 object-cover"
                            onError={(e) => {
                              e.target.onerror = null
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="200"%3E%3Crect width="400" height="200" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="16" fill="%239ca3af"%3EImage non disponible%3C/text%3E%3C/svg%3E'
                            }}
                          />
                        ) : (
                          <div className="w-full h-48 bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
                            Image non disponible
                          </div>
                        )}
                        <div className="absolute top-2 left-2">
                          {banner.status === 2 && <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">EN LIGNE</span>}
                          {banner.status === 1 && <span className="bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded">EN ATTENTE</span>}
                          {banner.status === 3 && <span className="bg-gray-600 text-white text-xs font-bold px-2 py-1 rounded">ARCHIVÉE</span>}
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-[#002A4E] text-lg mb-1">{banner.name}</h3>
                        {banner.description && (
                          <p className="text-gray-600 text-sm mb-2 line-clamp-2">{banner.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {banner.date}
                          </span>
                          {banner.link && (
                            <a href={banner.link} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 text-blue-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              Lien
                            </a>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/banner/${encodeId(banner.id)}`)}
                            className="flex-1 p-2 bg-green-50 text-green-600 rounded-md hover:bg-green-100 transition-colors flex items-center justify-center"
                            title="Voir"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => navigate(`/edit-banner/${banner.id}`)}
                            className="flex-1 p-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors flex items-center justify-center"
                            title="Modifier"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleArchivePost(banner.id, banner.status === 3)}
                            className="flex-1 p-2 bg-orange-50 text-orange-600 rounded-md hover:bg-orange-100 transition-colors flex items-center justify-center"
                            title={banner.status === 3 ? 'Désarchiver' : 'Archiver'}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(banner.id)}
                            className="flex-1 p-2 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors flex items-center justify-center"
                            title="Supprimer"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Onglet Vidéos */}
        {activeTab === 'videos' && (
          <div className="rounded-md bg-white ring-1 ring-slate-200/70 mb-6 p-3 sm:p-6">
            <div>
              {/* Sous-onglets pour filtrer les vidéos */}
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setVideoFilter('all')}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold transition-all ${
                      videoFilter === 'all'
                        ? 'bg-[#002A4E] text-white shadow-sm'
                        : 'bg-white text-slate-600 hover:text-[#002A4E] border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Tous
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myVideos.length}
                    </span>
                  </button>
                  <button
                    onClick={() => setVideoFilter('online')}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold transition-all ${
                      videoFilter === 'online'
                        ? 'bg-[#002A4E] text-white shadow-sm'
                        : 'bg-white text-slate-600 hover:text-[#002A4E] border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    En ligne
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myVideos.filter(v => v.status === 2).length}
                    </span>
                  </button>
                  <button
                    onClick={() => setVideoFilter('completed')}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold transition-all ${
                      videoFilter === 'completed'
                        ? 'bg-[#002A4E] text-white shadow-sm'
                        : 'bg-white text-slate-600 hover:text-[#002A4E] border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    En attente
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myVideos.filter(v => v.status === 1).length}
                    </span>
                  </button>
                  <button
                    onClick={() => setVideoFilter('archived')}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold transition-all ${
                      videoFilter === 'archived'
                        ? 'bg-[#002A4E] text-white shadow-sm'
                        : 'bg-white text-slate-600 hover:text-[#002A4E] border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    Archivés
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myVideos.filter(v => v.status === 3).length}
                    </span>
                  </button>
                </div>

                <div className='flex w-full items-center justify-between gap-3 mt-4'>
                  <h2 className="text-lg sm:text-2xl font-bold text-[#002A4E] leading-tight">
                    Mes Vidéos
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      ({myVideos.length})
                    </span>
                  </h2>
                  <button
                    onClick={() => navigate('/create-video')}
                    className="flex-shrink-0 flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-[#FFD700] text-[#002A4E] font-bold rounded-md hover:bg-[#F5C800] transition-colors shadow-md text-sm sm:text-base"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="whitespace-nowrap">Nouvelle Vidéo</span>
                  </button>
                </div>
                
              </div>

              {loadingVideos ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-white rounded-md overflow-hidden shadow-sm border border-gray-100">
                      <div className="h-48 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse"></div>
                      <div className="p-4 space-y-2">
                        <div className="h-5 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : myVideos.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-500 text-lg mb-4">Aucune vidéo</p>
                  <button 
                    onClick={() => navigate('/create-video')}
                    className="px-6 py-3 bg-[#FFD700] text-[#002A4E] font-bold rounded-md hover:bg-[#F5C800] transition-colors"
                  >
                    Créer votre première vidéo
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myVideos.filter(video => {
                    if (videoFilter === 'all') return true
                    if (videoFilter === 'online') return video.status === 2
                    if (videoFilter === 'completed') return video.status === 1
                    if (videoFilter === 'archived') return video.status === 3
                    return true
                  }).map((video) => (
                    <div
                      key={video.id}
                      className={`bg-white rounded-md overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow ${
                        video.status === 3 ? 'opacity-60 bg-gray-100' : ''
                      }`}
                    >
                      <div className="relative h-48 bg-gray-900 cursor-pointer" onClick={() => navigate(`/video/${encodeId(video.id)}`)}>
                        {video.thumbnail ? (
                          <img
                            src={video.thumbnail}
                            alt={video.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none' }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-16 h-16 text-white opacity-50" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center hover:bg-black/40 transition-colors">
                          <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-[#D4AF37] ml-1" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </div>
                        {/* Badge durée ou status */}
                        {video.status === 3 && (
                          <div className="absolute top-2 left-2 bg-gray-600 text-white text-xs font-bold px-2 py-1 rounded">
                            Archivé
                          </div>
                        )}
                        {video.status === 1 && (
                          <div className="absolute top-2 left-2 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded">
                            En attente
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-[#002A4E] text-lg mb-2 line-clamp-2">{video.name}</h3>
                        <p className="text-gray-600 text-sm line-clamp-2 mb-3">{video.description}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {video.views || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"/>
                            </svg>
                            {video.likes || 0}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/video/${encodeId(video.id)}`)
                            }}
                            className="flex-1 p-2 bg-green-50 text-green-600 rounded-md hover:bg-green-100 transition-colors flex items-center justify-center gap-1"
                            title="Voir"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/edit-video/${video.id}`)
                            }}
                            className="flex-1 p-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                            title="Modifier"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation()
                              handleArchivePost(video.id, video.status === 3)
                            }}
                            className="flex-1 p-2 bg-orange-50 text-orange-600 rounded-md hover:bg-orange-100 transition-colors flex items-center justify-center gap-1"
                            title={video.status === 3 ? "Désarchiver" : "Archiver"}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteProduct(video.id)
                            }}
                            className="flex-1 p-2 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors flex items-center justify-center gap-1"
                            title="Supprimer"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-md shadow-2xl max-w-md w-full mx-4 animate-scaleIn">
            <div className="p-2 sm:p-6">
              <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-center text-[#002A4E] mb-2">Supprimer cet article ?</h3>
              <p className="text-center text-gray-600 mb-6">
                Cette action est irréversible. L'article sera définitivement supprimé de votre compte.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={cancelDelete}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-md hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-3 bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 transition-colors shadow-md"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'archivage/désarchivage */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-md shadow-2xl max-w-md w-full mx-4 animate-scaleIn">
            <div className="p-2 sm:p-6">
              <div className={`flex items-center justify-center w-16 h-16 ${isUnarchiving ? 'bg-blue-100' : 'bg-orange-100'} rounded-full mx-auto mb-4`}>
                <svg className={`w-8 h-8 ${isUnarchiving ? 'text-blue-600' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-center text-[#002A4E] mb-2">
                {isUnarchiving ? 'Désarchiver cet article ?' : 'Archiver cet article ?'}
              </h3>
              <p className="text-center text-gray-600 mb-6">
                {isUnarchiving 
                  ? "L'article sera de nouveau visible dans vos articles en ligne."
                  : "L'article ne sera plus visible publiquement mais vous pourrez le restaurer plus tard."}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={cancelArchive}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-md hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmArchive}
                  className={`flex-1 px-4 py-3 text-white font-semibold rounded-md transition-colors shadow-md ${
                    isUnarchiving 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-orange-600 hover:bg-orange-700'
                  }`}
                >
                  {isUnarchiving ? 'Désarchiver' : 'Archiver'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

// ---------------------------------------------------------------------------
// Icônes du tableau de bord
// ---------------------------------------------------------------------------
const DASH_PATHS = {
  box: 'M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z',
  eye: 'M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  heart: 'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z',
  chat: 'M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z',
  users: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
  image: 'M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A1.5 1.5 0 0021.75 19.5V4.5A1.5 1.5 0 0020.25 3H3.75A1.5 1.5 0 002.25 4.5v15A1.5 1.5 0 003.75 21zM15 8.25h.008v.008H15V8.25z',
  banner: 'M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38a1.125 1.125 0 01-1.536-.48 20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46',
  video: 'M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9A2.25 2.25 0 0013.5 5.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z',
  cart: 'M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z',
  plus: 'M12 4.5v15m7.5-7.5h-15',
}

const DashIcon = ({ d, className = 'w-[18px] h-[18px]', stroke = 1.8 }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
)

export default MyDashboardPage